import { _ } from "../../../core/polyglot/polyglot.js";
import {
	formatAsDashedYearMonthDay,
	formatAsMonthAndYear,
	formatAsShortDate,
} from "../../../core/helpers/date-formatting-util.js";
import { hasFocusFor } from "../../../core/documentFocus.js";
import { makeMonitorRepository } from "../../../core/repositories/repositoryBuilders.js";
import accountingDocument from "../../shared/accountingDocument.js";
import AcknowledgementBanner from "./AcknowledgementBanner.js";
import DisclaimerIconWidget from "../../../core/widgets/DisclaimerIconWidget/DisclaimerIconWidget.js";
import getKeyFigureService from "../../../core/services/getKeyFigureService.js";
import KeyFigureContextRepositoryWrapper from "../../../core/repositories/KeyFigureContextRepositoryWrapper.js";
import makeReportNotificationButton from "./makeReportNotificationButton.js";
import optionsBag from "../../../models/options/optionsBag.js";
import PeriodReport from "../../../models/accounting/periodReport/PeriodReport.js";
import periodReportCommentWidget from "./periodReportCommentWidget.js";
import PeriodReportEventLogWidget from "./PeriodReportEventLogWidget/PeriodReportEventLogWidget.js";
import PeriodReportNotificationService from "./PeriodReportNotificationService.js";
import PeriodReportPDFExportService from "./pdfExport/PeriodReportPDFExportService.js";
import PeriodReportSectionWidgetVisitor from "./PeriodReportSectionWidgetVisitor.js";
import PeriodReportService from "./PeriodReportService.js";
import popoverActionButtonWidget from "../../../core/widgets/popoverActionButtonWidget.js";
import regionWidget from "../../../core/widgets/regionWidget.js";
import ReportDisclaimerWidget from "../../../core/widgets/ReportDisclaimerWidget.js";
import responsive from "../../../core/responsive.js";
import sectionWidget from "../../../core/widgets/sectionWidget.js";
import session from "../../../core/session.js";
import theme from "../../../core/theme.js";
import UploadService from "../../../core/services/UploadService.js";

const configuration = {
	viewedDelay: 2000,
};

/**
 * Configuration for the period report viewer.
 *
 * @param {number} viewedDelay - Number of millisecond a page has to have focus to be considered as "Viewed"
 */
export function setConfiguration({ viewedDelay }) {
	configuration.viewedDelay = viewedDelay;
}

/**
 * `periodReportViewerDocument` is the document used to display a period report.
 */
const periodReportViewerDocument = accountingDocument.subclass((that, my) => {
	my.initialize = function () {
		my.super(...arguments);

		my.report = null;
		my.companyUsers = [];
		my.agencyUsers = [];

		my.periodReportService = new PeriodReportService();

		my.lastImportDate = formatAsShortDate(
			session.getCompany().getLastImportDate(),
		);

		const companyId = session.getCompany().getLegacyId();

		my.periodReportRepository = new KeyFigureContextRepositoryWrapper({
			keyFigureService: getKeyFigureService(),
			repository: makeMonitorRepository({
				model: PeriodReport,
				route: "/Accounting2/Company/#companyId/PeriodReport/?action/?id",
				defaultFilters: { companyId },
				antiForgeryToken: session.getAntiForgeryToken(),
			}),
		});

		my.notificationService = new PeriodReportNotificationService({
			companyId,
		});

		my.periodReportNotificationFeatureToggle = optionsBag.fromId(
			"period-report-email-notification",
		);

		my.sectionWidgetVisitor = new PeriodReportSectionWidgetVisitor({
			canViewAccountingDocuments: session
				.getCurrentUser()
				.canViewAccountingDocuments(session.getCompany()),
			lastImportDate: my.lastImportDate ?? "",
		});

		that.title = regionWidget({
			content: (html) => {
				if (my.report) {
					html.render(
						my.report.getTitle(),
						optionsBag.fromId("disclaimer").renderWhenActive(
							new DisclaimerIconWidget({
								company: session.getCompany(),
								period: session.getPeriod(),
								agency: session.agency,
								date: my.report.getPublishDate(),
							}),
						),
					);
				} else {
					html.render(_("Period Report"));
				}
			},
		});

		that.subtitle = regionWidget({
			content: (html) => {
				if (!my.report) {
					return;
				}
				if (my.report.getSubtitle()) {
					html.render(my.report.getSubtitle());
				} else {
					html.render(
						_("Made by {author} in {date} for {company}", {
							author: (html) => {
								html.em(my.report.getAuthor().getName());
							},
							date: formatAsMonthAndYear(
								my.report.getLastUpdateDate(),
							),
							company: (html) => {
								html.strong(my.report.getCompany().getName());
							},
						}),
					);
				}
			},
		});

		that.documentId = "period-report";

		that.hasHelp = function () {
			return false;
		};

		that.showEmpty();
	};

	my.initializeSubwidgets = function () {
		my.super(...arguments);

		my.reportDisclaimerPrint = new ReportDisclaimerWidget({
			company: session.getCompany(),
			agency: session.agency,
			period: session.getPeriod(),
		});

		my.comment = periodReportCommentWidget();

		my.isAcknowledgementEnabled = optionsBag
			.fromId("acknowledge-period-report")
			.isActive();

		my.acknowledgementBanner = new AcknowledgementBanner({
			onClick: () => {
				my.setReportAsAcknowledged().then(() => {
					my.acknowledgementBanner = null;
					that.update();
				});
			},
		});
	};

	that.hasPeriodPicker = function () {
		return false;
	};

	that.actions = function () {
		const actions = [
			my.rightMenuSection(_("Export"), (html) => {
				html.a(
					{ klass: "btn btn-block btn-default" },
					_("Print report"),
				).click(() => {
					window.print();
				});

				html.a(
					{
						klass: "btn btn-block btn-default",
						"data-test": "pdf-export",
					},
					_("Export to PDF"),
				).click(() => {
					new PeriodReportPDFExportService({
						periodReport: my.report,
					}).download(`${my.report.getTitle()}.pdf`);

					// FTGP-8870: As stitching generated attachments is
					// disabled, download them separately.
					my.report
						.getAttachments()
						.filter(
							(attachment) =>
								attachment.isDefaultAttachment() ||
								attachment.isGeneratedAttachment(),
						)
						.map((attachment) => my.downloadAttachment(attachment));
				});
			}),
		];
		if (
			session
				.getCurrentUser()
				.canCreatePeriodReports(session.getCompany())
		) {
			const canCreateReports = session
				.getCurrentUser()
				.canCreatePeriodReports(session.getCompany());
			const canWithdrawReport =
				canCreateReports && my.report && my.report.isWithdrawable();
			const canViewEventLog = my.report && canCreateReports;
			const canSendNotification =
				my.report &&
				!my.report.isDraft() &&
				!my.report.isWithdrawn() &&
				canCreateReports;
			if (canWithdrawReport) {
				actions.push(
					my.rightMenuSection(_("Manage report"), (html) => {
						html.render(
							html
								.a(
									{ klass: "btn btn-block btn-default" },
									_("Withdraw report"),
								)
								.click(my.withdrawReport),
						);
					}),
				);
			}
			if (my.report && canCreateReports) {
				actions.push(
					my.rightMenuSection(_("Create report"), (html) => {
						html.render(
							html.a(
								{
									klass: "btn btn-block btn-default",
									click: () =>
										my.copyReportAndRedirect(my.report),
								},
								_("New report from this report"),
							),
						);
					}),
				);
			}
			if (canViewEventLog) {
				actions.push(
					my.rightMenuSection(
						_("Event Log"),
						my.renderEventLogPopoverActionButtonOn,
						my.renderViewEventLogSummaryOn,
					),
				);
			}
			if (canSendNotification) {
				my.periodReportNotificationFeatureToggle.whenActive(() => {
					actions.push(my.renderMailNotificationButtonOn);
				});
			}
		}

		return actions;
	};

	that.show = async function (reportId) {
		that.showUpdating();

		// The Repository class uses own custom promises.
		// We are trying to combine them with Promise.all() which expects native promises.
		// Passing our own promises to Promise.all() makes it impossible to handle promise rejection.
		// Below is an ugly hack to circumvent the issue.
		// If we give Damien some time to rewrite the repositories then we won't have to do this anymore.
		const reportPromise = new Promise((resolve) => {
			my.periodReportRepository
				.get({
					filter: {
						id: reportId,
					},
				})
				.then((report) => resolve(report))
				.catch(() => {
					if (
						my.report !== null &&
						(!my.report.isWithdrawn() ||
							session
								.getCurrentUser()
								.canViewWithdrawnPeriodReports(
									session.getCompany(),
								))
					) {
						return;
					}
					my.redirectTo("period-reports");
				});
		});

		const promises = [
			reportPromise,
			my.notificationService.fetchCompanyUsers(),
			my.notificationService.fetchCompanyAgencyUsers(),
		];

		const [periodReport, companyUsers, agencyUsers] =
			await Promise.all(promises);

		my.setPeriodReport(periodReport);
		my.companyUsers = companyUsers;
		my.agencyUsers = agencyUsers;
		my.updateActions();

		if (!(await my.periodReportPdfExist())) {
			my.export();
		}
	};

	my.periodReportPdfExist = function () {
		const periodReportPdfRepository = makeMonitorRepository({
			route: "/Accounting2/Company/#companyId/PeriodReport/#periodReportId/Pdf",
			defaultFilters: {
				companyId: session.getCompany().getLegacyId(),
				periodReportId: my.report.getId(),
			},
		});

		return periodReportPdfRepository.head();
	};

	my.export = function () {
		new PeriodReportPDFExportService({
			periodReport: my.report,
			overlayContent: (html) =>
				html.div(
					{ klass: "pdf-export-overlay-contents" },
					html.h2(_("please wait…")),
				),
		}).export((pdf) => {
			const uploadService = new UploadService({
				uploadPath: `/Accounting2/Company/${session.getCompany().getLegacyId()}/PeriodReport/${my.report.getId()}/Pdf/`,
			});

			pdf.getBlob().then((pdfBlob) => {
				pdfBlob.name = "period-report.pdf";
				return uploadService.uploadFile(pdfBlob);
			});
		});
	};

	my.renderFooterOn = function (html) {
		if (!my.shouldShowFooter()) {
			return;
		}

		html.div({ klass: "report-floating-footer" }, my.acknowledgementBanner);
	};

	my.renderContentOn = function (html) {
		html.render(
			my.logoRenderer(),
			my.publicationDateRenderer(),
			my.commentRenderer(),
			my.reportDisclaimerPrint,
			my.sectionsRenderer(),
		);

		my.renderFooterOn(html);
	};

	my.logoRenderer = function () {
		return (html) => {
			if (!theme.agencyPrintLogo) {
				return;
			}

			html.div(
				{
					klass: "report-agency-logo print-only",
					"data-test": "report-agency-logo",
				},
				html.img({ src: theme.agencyPrintLogo }),
			);
		};
	};

	my.sectionsRenderer = function () {
		return my.sectionWidgetVisitor.visitAll(
			my.report.getSections().filter((section) => section.isEnabled()),
		);
	};

	my.publicationDateRenderer = function () {
		return (html) => {
			const publishDate = my.report.getPublishDate();

			if (!publishDate) {
				return;
			}

			html.p(
				_("Publish date: {date}", {
					date: formatAsDashedYearMonthDay(publishDate),
				}),
			);
		};
	};

	my.commentRenderer = function () {
		return sectionWidget({
			cssClass: "comment-section",
			content: my.comment,
			dataTestName: "main-comment",
		});
	};

	my.renderEventLogPopoverActionButtonOn = function (html) {
		const events = my.report.getEventLog().getNonViewEvents();
		const eventLog = new PeriodReportEventLogWidget({ events });

		const popoverButton = popoverActionButtonWidget({
			label: _("Show history"),
			popoverHeader: _("Timeline"),
			popoverContent: eventLog,
		});

		that.onDetach.register(popoverButton.willDetach);
		html.render(popoverButton);
	};

	my.renderViewEventLogSummaryOn = function (html) {
		const events = my.report.getEventLog().getViewEvents();

		if (!events.length) {
			return;
		}

		const popoverButton = popoverActionButtonWidget({
			label: my.makeViewLogEventsText(events),
			cssClass: "link-button",
			dataTestName: "views-log",
			popoverHeader: _("View history"),
			popoverContent: new PeriodReportEventLogWidget({ events }),
		});

		that.onDetach.register(popoverButton.willDetach);
		html.render(popoverButton);
	};

	my.makeViewLogEventsText = function (events) {
		const author = events.at(-1).getAuthor();

		if (author.isUnnamed()) {
			return _("Someone viewed this report");
		}

		const name = author.getName();

		return events.length === 1
			? _("{name} viewed this report", { name })
			: _("{name} and {number} others viewed this report", {
					name,
					number: events.length - 1,
				});
	};

	my.renderMailNotificationButtonOn = function (html) {
		const company = session.getCompany();

		const service = new PeriodReportNotificationService({
			companyId: company.getLegacyId(),
		});

		html.render(
			my.rightMenuSection(
				_("Notify users"),
				makeReportNotificationButton({
					currentUser: session.getCurrentUser(),
					companyUsers: my.companyUsers,
					agencyUsers: my.agencyUsers,
					report: my.report,
					company,
					cssClass: "btn-block btn-default",
					sendNotifications: (emailContent) =>
						service.sendNotifications({
							report: my.report,
							emailContent,
						}),
				}),
			),
		);
	};

	/**
	 * Register hooks on the page to know when the page got focus long
	 * enough to be flagged as "Viewed".
	 *
	 * @return {Promise<void>}
	 */
	my.registerForVisibility = async function () {
		const currentUser = session.getCurrentUser();
		if (!currentUser.canRegisterViewEventForReport()) {
			return;
		}

		if (my.hasSendAViewNotification) {
			return;
		}

		const currentLocation = window.location.toString();

		await hasFocusFor(configuration.viewedDelay);

		if (currentLocation !== window.location.toString()) {
			// if the page changed in between.
			// We omit the case of going back and forth in less that
			// 2 seconds.
			return;
		}
		try {
			await my.periodReportRepository.update({
				model: my.report,
				notify: false,
				readResponse: false,
				filter: {
					action: "View",
					id: my.report.getId(),
				},
			});

			my.hasSendAViewNotification = true;
		} catch {
			my.registerForVisibility();
		}
	};

	my.shouldShowFooter = function () {
		if (
			!my.isAcknowledgementEnabled ||
			!my.report ||
			!my.acknowledgementBanner
		) {
			return false;
		}

		const currentUser = session.getCurrentUser();
		const alreadyAcknowledged =
			my.report.hasBeenAcknowledgedBy(currentUser);
		if (alreadyAcknowledged) {
			return false;
		}

		return session.getCurrentUser().isCompanyUser();
	};

	my.setPeriodReport = function (report) {
		my.report = report;
		my.hasSendAViewNotification = false;
		my.comment.setAuthor(report.getAuthor());
		my.comment.setComment(report.getComment());
		my.reportDisclaimerPrint.setDate(report.getPublishDate());

		that.title.update();
		that.subtitle.update();

		my.updateActions();

		if (
			that.actions().length &&
			!responsive.isMobileOrIPadPortaitBreakpoint()
		) {
			my.showActions();
		}

		that.showContent();
		my.registerForVisibility();
	};

	my.setReportAsAcknowledged = function () {
		return my.periodReportRepository.update({
			model: my.report,
			filter: {
				action: "Acknowledge",
				id: my.report.getId(),
			},
		});
	};

	my.withdrawReport = function () {
		my.confirmDeletion({
			title: _("Withdraw {report}?", {
				report: my.report.getTitle(),
			}),
			body: _("Withdrawing a report cannot be undone."),
			okLabel: _("Withdraw"),
			callback: () =>
				my.periodReportRepository
					.update({
						model: my.report,
						filter: {
							action: "Withdraw",
							id: my.report.getId(),
						},
					})
					.then(() => my.redirectTo("period-reports")),
		});
	};

	my.downloadAttachment = function (attachment) {
		const anchor = document.createElement("a");
		const path = `/Shared/AttachmentContent/${attachment.getId()}`;
		anchor.setAttribute("download", path);
		anchor.setAttribute("href", path);
		anchor.setAttribute("target", "_blank");
		anchor.setAttribute("style", "display: none;");

		document.body.appendChild(anchor);
		anchor.click();
		document.body.removeChild(anchor);
	};

	my.copyReportAndRedirect = async function (report) {
		const newDraft = await my.periodReportService.copyReport(report);
		my.redirectTo("edit-period-report", { id: newDraft.getId() });
	};
});

export default periodReportViewerDocument;
